import { toastService } from '@thefarmersfront/kitchen';

import { DefaultError, UseMutationOptions, useMutation } from '@tanstack/react-query';

import { TOAST_MESSAGE } from '~/constants';

type DownloadContext = { toastId: string };

type DownloadMutationOptions<TData, TError, TVariables> = Omit<
  UseMutationOptions<TData, TError, TVariables, DownloadContext>,
  'mutationFn' | 'onMutate' | 'onSuccess' | 'onError'
> & {
  /**
   * 다운로드 중 표시할 메시지
   * @default TOAST_MESSAGE.DOWNLOADING
   */
  downloadingMessage?: string;
  onSuccess?: (data: TData, variables: TVariables, context?: DownloadContext) => void;
  onError?: (error: TError, variables: TVariables, context?: DownloadContext) => void;
};

/**
 * 다운로드 전용 Mutation Hook
 * - 다운로드 시작 시 "다운로드 중" 토스트 표시
 * - 완료/실패 시 토스트 자동 제거
 *
 * @example
 * const downloadMutation = useDownloadMutation(
 *   (params) => downloadExcel(params),
 *   {
 *     onSuccess: () => toastService.addToast({ message: '다운로드 완료' }),
 *     onError: (error) => toastService.addToast({ type: 'error', message: error.message }),
 *   }
 * );
 *
 * downloadMutation.mutate({ id: 1 });
 */
export const useDownloadMutation = <TData = void, TError = DefaultError, TVariables = void>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options?: DownloadMutationOptions<TData, TError, TVariables>
) => {
  const { downloadingMessage = TOAST_MESSAGE.DOWNLOADING, onSuccess, onError, ...restOptions } = options ?? {};

  return useMutation<TData, TError, TVariables, DownloadContext>({
    mutationFn,
    onMutate: () => {
      const toastId = toastService.addToast({
        message: downloadingMessage,
        persist: true,
      });
      return { toastId };
    },
    onSuccess: (data, variables, context) => {
      if (context?.toastId) {
        toastService.removeToast(context.toastId);
      }
      onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      if (context?.toastId) {
        toastService.removeToast(context.toastId);
      }
      onError?.(error, variables, context);
    },
    ...restOptions,
  });
};

export default useDownloadMutation;
